import type { Multilingual } from '../lib/marketingLocale'

/**
 * Landing hero + pricing + "why switch" copy, in all six (playbook WS1.1).
 *
 * SIX languages here, TWO on the paired documents — decision D3, 2026-08-19.
 * The landing is a single URL rendered in the visitor's own language, so adding
 * a language costs a translation and nothing else: no URL pair, no hreflang, no
 * prerender route, no OG card. /press, /legal/subprocessors, /founding and the
 * comparison pages stay EN/DE, because each of those languages is another
 * indexable URL carrying compliance copy someone has to stand behind.
 *
 * fr/it/es/pt were translated by model in this pass and ship pre-go-live on
 * purpose (nobody is reading them yet); a native review before go-live is a
 * PRE-GOLIVE item in docs/backlog.md.
 *
 * `hu` (RB-12 C4, D26) is the seventh and the exception to "no URL, no
 * hreflang": it also has a market URL of its own, `/hu`. Its copy is
 * AI-drafted under the RB-12 calibration (formal Ön, R1) and pending
 * professional language review (IN-205); the extraction sheet it was drafted
 * from is `agentic-testing-harness/runs/rb12-hu/landing-strings.json`.
 *
 * Prices are never typed into this file. The hero body and the price lines
 * are templates filled from the CHF price table in lib/landingPrices.ts, so
 * no translation carries a number or a currency of its own.
 *
 * Register: de/it informal (du / tu), fr/es formal (vous / usted), pt informal
 * (você) — the same register each language uses in the app's own locale bundle,
 * because a visitor who registers should not be addressed differently on the
 * next screen.
 *
 * Claims discipline — every line here has to be checkable by the reader:
 *
 *  - Hosting has a ceiling and the phrases above it are FORBIDDEN. The
 *    application and database run at Hetzner in Germany and object storage in
 *    Backblaze B2's EU region in the Netherlands (verified 2026-08-19), so
 *    "application, database and your files are hosted in the EU" is true and is
 *    what we say. "Swiss hosting" is not — nothing runs in Switzerland.
 *    "Data never leaves the EU" is not — transactional email transits Resend in
 *    the US. "EU-sovereign" is not — several vendors in the chain are
 *    US-incorporated, and residency is not sovereignty.
 *  - No invented metrics. The previous hero's "0 emails lost" and "30s to
 *    quote" were numbers nobody measured; they are gone. What replaced them
 *    are statements a prospect can verify in a 30-day trial.
 *  - The landing page makes NO comparative claim and names NO competitor. The
 *    page once carried one hedged uniqueness sentence ("we haven't found
 *    another suite that ships QR-bill invoicing and galleries in the same
 *    subscription"); it was dropped in the 2026-08-19 copy pass because even
 *    hedged it is unverifiable, it becomes false the day someone ships it, and
 *    it re-emphasised the Swiss angle this page is deliberately demoting. The
 *    /compare/* pages are where a competitor gets named, with a verified price,
 *    a "not found on their public pages" hedge and a fair acknowledgement.
 *
 * marketingSurfaces.test.ts enforces all of the above.
 */

export interface HeroCopy {
  /** Badge chips above the headline. */
  chips: string[]
  /** Headline, split so the second half keeps the existing gradient treatment. */
  headlineLead: string
  headlineAccent: string
  /** The bold statement under the headline. */
  subheadline: string
  /**
   * Supporting paragraph — the playbook's price/feature/hosting line.
   *
   * A template, not a string: `prices` is the monthly ladder already formatted
   * (`CHF 18/29/37` — see lib/landingPrices.ts), so no translation ever carries
   * a number or a currency of its own.
   */
  body: (prices: string) => string
  secondaryCta: string
  /** Fine print under the CTA row. */
  reassurance: string
}

export const HERO: Multilingual<HeroCopy> = {
  en: {
    chips: ['Built by a working photographer in Zürich', 'Mobile-first'],
    headlineLead: 'Run your whole studio',
    headlineAccent: 'in one place.',
    subheadline:
      'Clients, booking, galleries, offers, contracts, invoices — built by a photographer, made in Switzerland.',
    body: (prices) =>
      `${prices} per month. Every feature in every tier. VAT invoicing built in, including Swiss QR-bill. Application, database and your files hosted in the EU. 30 days free, no credit card.`,
    secondaryCta: 'See how it works',
    reassurance: 'No credit card · 30-day trial · Cancel anytime',
  },
  de: {
    chips: ['Von einem Fotografen in Zürich gebaut', 'Mobile-first'],
    headlineLead: 'Dein ganzes Studio',
    headlineAccent: 'an einem Ort.',
    subheadline:
      'Kund:innen, Buchungen, Galerien, Offerten, Verträge, Rechnungen — von einem Fotografen gebaut, made in Switzerland.',
    body: (prices) =>
      `${prices} pro Monat. Alle Funktionen in jedem Abo. MWST-Rechnungen inklusive, mit Schweizer QR-Rechnung. Anwendung, Datenbank und deine Dateien in der EU gehostet. 30 Tage gratis, ohne Kreditkarte.`,
    secondaryCta: 'So funktioniert’s',
    reassurance: 'Ohne Kreditkarte · 30 Tage testen · Jederzeit kündbar',
  },
  fr: {
    chips: ['Créé par un photographe en activité à Zurich', 'Mobile-first'],
    headlineLead: 'Gérez tout votre studio',
    headlineAccent: 'au même endroit.',
    subheadline:
      'Clients, réservations, galeries, offres, contrats, factures — créé par un photographe, made in Switzerland.',
    body: (prices) =>
      `${prices} par mois. Toutes les fonctionnalités dans chaque formule. Facturation TVA intégrée, QR-facture suisse comprise. Application, base de données et vos fichiers hébergés dans l’UE. 30 jours gratuits, sans carte de crédit.`,
    secondaryCta: 'Voir comment ça marche',
    reassurance: 'Sans carte de crédit · 30 jours d’essai · Résiliable à tout moment',
  },
  it: {
    chips: ['Creato da un fotografo che lavora a Zurigo', 'Mobile-first'],
    headlineLead: 'Gestisci tutto il tuo studio',
    headlineAccent: 'in un unico posto.',
    subheadline:
      'Clienti, prenotazioni, gallerie, preventivi, contratti, fatture — creato da un fotografo, made in Switzerland.',
    body: (prices) =>
      `${prices} al mese. Tutte le funzioni in ogni piano. Fatturazione IVA inclusa, con QR-fattura svizzera. Applicazione, database e i tuoi file ospitati nell’UE. 30 giorni gratis, senza carta di credito.`,
    secondaryCta: 'Scopri come funziona',
    reassurance: 'Senza carta di credito · 30 giorni di prova · Disdici quando vuoi',
  },
  es: {
    chips: ['Creado por un fotógrafo en activo en Zúrich', 'Mobile-first'],
    headlineLead: 'Gestione todo su estudio',
    headlineAccent: 'en un solo lugar.',
    subheadline:
      'Clientes, reservas, galerías, presupuestos, contratos, facturas — creado por un fotógrafo, made in Switzerland.',
    body: (prices) =>
      `${prices} al mes. Todas las funciones en todos los planes. Facturación con IVA incluida, con la factura QR suiza. Aplicación, base de datos y sus archivos alojados en la UE. 30 días gratis, sin tarjeta de crédito.`,
    secondaryCta: 'Vea cómo funciona',
    reassurance: 'Sin tarjeta de crédito · 30 días de prueba · Cancele cuando quiera',
  },
  pt: {
    chips: ['Criado por um fotógrafo atuante em Zurique', 'Mobile-first'],
    headlineLead: 'Cuide de todo o seu estúdio',
    headlineAccent: 'em um só lugar.',
    subheadline:
      'Clientes, agendamentos, galerias, orçamentos, contratos, faturas — criado por um fotógrafo, made in Switzerland.',
    body: (prices) =>
      `${prices} por mês. Todos os recursos em todos os planos. Faturamento com IVA incluído, com QR-bill suíço. Aplicação, banco de dados e seus arquivos hospedados na UE. 30 dias grátis, sem cartão de crédito.`,
    secondaryCta: 'Veja como funciona',
    reassurance: 'Sem cartão de crédito · 30 dias de teste · Cancele quando quiser',
  },
  hu: {
    chips: ['Egy Zürichben dolgozó fotós fejlesztette', 'Mobilra tervezve'],
    headlineLead: 'Vezesse egész stúdióját',
    headlineAccent: 'egy helyen.',
    subheadline:
      'Ügyfelek, foglalások, galériák, ajánlatok, szerződések, számlák – egy fotós fejlesztette, Svájcban.',
    body: (prices) =>
      `${prices} havonta. Minden csomagban minden funkció. Beépített ÁFA-s számlázás, svájci QR-számlával. Az alkalmazás, az adatbázis és az Ön fájljainak tárhelye az EU-ban van. 30 napig ingyenes, bankkártya nélkül.`,
    secondaryCta: 'Így működik',
    reassurance: 'Bankkártya nélkül · 30 napos próbaidő · Bármikor lemondható',
  },
  nl: {
    chips: ['Gebouwd door een werkende fotograaf in Zürich', 'Mobile-first'],
    headlineLead: 'Beheer uw hele studio',
    headlineAccent: 'op één plek.',
    subheadline:
      'Klanten, boekingen, galerijen, offertes, contracten, facturen – gebouwd door een fotograaf, made in Switzerland.',
    body: (prices) =>
      `${prices} per maand. Alle functies in elk abonnement. Btw-facturatie ingebouwd, inclusief Zwitserse QR-factuur. Applicatie, database en uw bestanden gehost in de EU. 30 dagen gratis, zonder creditcard.`,
    secondaryCta: 'Bekijk hoe het werkt',
    reassurance: 'Zonder creditcard · 30 dagen proberen · Op elk moment opzegbaar',
  },
  pl: {
    chips: ['Stworzone przez fotografa pracującego w Zurychu', 'Mobile-first'],
    headlineLead: 'Całe studio',
    headlineAccent: 'w jednym miejscu.',
    subheadline:
      'Klienci, rezerwacje, galerie, oferty, umowy, faktury – stworzone przez fotografa, made in Switzerland.',
    body: (prices) =>
      `${prices} miesięcznie. Wszystkie funkcje w każdym pakiecie. Fakturowanie z VAT w standardzie, w tym szwajcarski QR-rachunek. Aplikacja, baza danych i pliki są hostowane w UE. 30 dni za darmo, bez karty kredytowej.`,
    secondaryCta: 'Zobacz, jak to działa',
    reassurance: 'Bez karty kredytowej · 30 dni na próbę · Rezygnacja w dowolnym momencie',
  },
  cs: {
    chips: ['Vytvořeno fotografem, který v Curychu denně pracuje', 'Mobile-first'],
    headlineLead: 'Celé studio',
    headlineAccent: 'na jednom místě.',
    subheadline:
      'Klienti, rezervace, galerie, nabídky, smlouvy, faktury – vytvořil fotograf, made in Switzerland.',
    body: (prices) =>
      `${prices} měsíčně. Všechny funkce v každém tarifu. Fakturace s DPH v ceně, včetně švýcarské QR-faktury. Aplikace, databáze i soubory jsou hostovány v EU. 30 dní zdarma, bez platební karty.`,
    secondaryCta: 'Podívejte se, jak to funguje',
    reassurance: 'Bez platební karty · 30 dní na vyzkoušení · Zrušení kdykoli',
  },
}

/**
 * The Register button label, in the landing's six languages.
 *
 * Deliberately NOT `t('common:landing.ctaRegister')`. Every other string on a
 * marketing surface comes from these content modules, but this one label was
 * still read out of the app's i18n namespaces — so its language followed the
 * logged-in member's `uiLocale` rather than the page. A member set to fr-CH saw
 * "S'inscrire" on the English landing page, and an Italian member reading
 * /de/founding got "Registrati" under German prose. The button is the same word
 * on the nav, the hero, the pricing trial card, the comparison CTA and both
 * founding CTAs, so it lives here once and every call site picks it with the
 * locale it already has (the route's on a paired page, `useMarketingLocale()`
 * on the landing).
 *
 * The app key stays in the six locale files — `/welcome-1` (legacy) still uses
 * it, and it is an in-app string there, not a marketing one. The six labels
 * below are deliberately the SAME words as `common:landing.ctaRegister` in each
 * locale file: the fix was that the label followed the wrong locale, not that
 * the translations were wrong, and a button that changes wording between the
 * landing and the app would be a second bug wearing the first one's clothes.
 */
export const CTA_REGISTER: Multilingual<string> = {
  en: 'Register',
  de: 'Registrieren',
  fr: 'S’inscrire',
  it: 'Registrati',
  es: 'Registrarse',
  pt: 'Cadastre-se',
  hu: 'Regisztráció',
  nl: 'Registreren',
  pl: 'Zarejestruj się',
  cs: 'Registrovat se',
}

/** Pipeline step labels — the hero visual and its mobile strip. */
export const PIPELINE_STEPS: Multilingual<{ long: string; short: string }[]> = {
  en: [
    { long: 'Inquiry arrives in your inbox', short: 'Inquiry' },
    { long: 'Quote, contract & questionnaire sent', short: 'Quote & Contract' },
    { long: 'Shoot scheduled · calendar synced · team notified', short: 'Shoot' },
    { long: 'Editing & culling tasks assigned', short: 'Editing' },
    { long: 'Gallery delivered to the client', short: 'Gallery' },
    { long: 'Invoice paid', short: 'Invoice' },
  ],
  de: [
    { long: 'Anfrage landet in deinem Posteingang', short: 'Anfrage' },
    { long: 'Offerte, Vertrag & Fragebogen verschickt', short: 'Offerte' },
    { long: 'Shooting geplant · Kalender synchronisiert · Team informiert', short: 'Shooting' },
    { long: 'Bearbeitungs- und Auswahl-Aufgaben verteilt', short: 'Bearbeitung' },
    { long: 'Galerie an die Kundschaft ausgeliefert', short: 'Galerie' },
    { long: 'Rechnung bezahlt', short: 'Rechnung' },
  ],
  fr: [
    { long: 'La demande arrive dans votre boîte de réception', short: 'Demande' },
    { long: 'Offre, contrat et questionnaire envoyés', short: 'Offre' },
    { long: 'Séance planifiée · calendrier synchronisé · équipe informée', short: 'Séance' },
    { long: 'Tâches de retouche et de tri attribuées', short: 'Retouche' },
    { long: 'Galerie livrée au client', short: 'Galerie' },
    { long: 'Facture payée', short: 'Facture' },
  ],
  it: [
    { long: 'La richiesta arriva nella tua posta in arrivo', short: 'Richiesta' },
    { long: 'Preventivo, contratto e questionario inviati', short: 'Preventivo' },
    { long: 'Servizio pianificato · calendario sincronizzato · team informato', short: 'Servizio' },
    { long: 'Attività di post-produzione e selezione assegnate', short: 'Post' },
    { long: 'Galleria consegnata al cliente', short: 'Galleria' },
    { long: 'Fattura pagata', short: 'Fattura' },
  ],
  es: [
    { long: 'La solicitud llega a su bandeja de entrada', short: 'Solicitud' },
    { long: 'Presupuesto, contrato y cuestionario enviados', short: 'Presupuesto' },
    { long: 'Sesión programada · calendario sincronizado · equipo avisado', short: 'Sesión' },
    { long: 'Tareas de edición y selección asignadas', short: 'Edición' },
    { long: 'Galería entregada al cliente', short: 'Galería' },
    { long: 'Factura pagada', short: 'Factura' },
  ],
  pt: [
    { long: 'O contato chega na sua caixa de entrada', short: 'Contato' },
    { long: 'Orçamento, contrato e questionário enviados', short: 'Orçamento' },
    { long: 'Ensaio agendado · calendário sincronizado · equipe avisada', short: 'Ensaio' },
    { long: 'Tarefas de edição e seleção atribuídas', short: 'Edição' },
    { long: 'Galeria entregue ao cliente', short: 'Galeria' },
    { long: 'Fatura paga', short: 'Fatura' },
  ],
  hu: [
    { long: 'Az érdeklődés a Postafiókba érkezik', short: 'Érdeklődés' },
    { long: 'Ajánlat, szerződés és kérdőív elküldve', short: 'Ajánlat' },
    { long: 'Fotózás ütemezve · naptár szinkronizálva · csapat értesítve', short: 'Fotózás' },
    { long: 'Utómunka- és képválogatási feladatok kiosztva', short: 'Utómunka' },
    { long: 'Galéria átadva az ügyfélnek', short: 'Galéria' },
    { long: 'Számla kifizetve', short: 'Számla' },
  ],
  nl: [
    { long: 'Aanvraag komt binnen in uw inbox', short: 'Aanvraag' },
    { long: 'Offerte, contract & vragenlijst verstuurd', short: 'Offerte' },
    { long: 'Shoot ingepland · agenda gesynchroniseerd · team geïnformeerd', short: 'Shoot' },
    { long: 'Bewerkings- en selectietaken toegewezen', short: 'Bewerking' },
    { long: 'Galerie opgeleverd aan de klant', short: 'Galerie' },
    { long: 'Factuur betaald', short: 'Factuur' },
  ],
  pl: [
    { long: 'Zapytanie trafia do skrzynki', short: 'Zapytanie' },
    { long: 'Oferta, umowa i kwestionariusz wysłane', short: 'Oferta' },
    { long: 'Sesja zaplanowana · kalendarz zsynchronizowany · zespół powiadomiony', short: 'Sesja' },
    { long: 'Zadania edycji i selekcji przydzielone', short: 'Edycja' },
    { long: 'Galeria dostarczona klientowi', short: 'Galeria' },
    { long: 'Faktura zapłacona', short: 'Faktura' },
  ],
  cs: [
    { long: 'Poptávka přichází do doručené pošty', short: 'Poptávka' },
    { long: 'Nabídka, smlouva a dotazník odeslány', short: 'Nabídka' },
    { long: 'Focení naplánováno · kalendář synchronizován · tým informován', short: 'Focení' },
    { long: 'Úkoly pro úpravy a výběr přiděleny', short: 'Úpravy' },
    { long: 'Galerie předána klientovi', short: 'Galerie' },
    { long: 'Faktura zaplacena', short: 'Faktura' },
  ],
}

export interface PricingCopy {
  heading: string
  intro: string
  /**
   * Compliance line — VAT / QR-bill / hosting.
   *
   * There is deliberately no sibling `comparative` field: the landing page
   * carries no uniqueness claim at all (see the module header), and leaving the
   * field on the type would be an open invitation to put one back.
   */
  compliance: string
  modules: string[]
  monthly: string
  annual: string
  /** `percent` is the best annual saving, computed from the price table. */
  saveUpTo: (percent: number) => string
  planNames: Record<'trial' | 'starter' | 'studio' | 'agency', string>
  planDescs: Record<'trial' | 'starter' | 'studio' | 'agency', string>
  /**
   * CTA label for the three PAID cards. The trial card keeps the shared
   * `CTA_REGISTER` label — it is the card that registers an account with no
   * plan attached, and that decision stays where it was.
   */
  planCtas: Record<'starter' | 'studio' | 'agency', string>
  trialPeriod: string
  perMonth: string
  featureAll: string
  featureStorage: (amount: string) => string
  featureSeats: (count: number) => string
  featureUnlimitedSeats: string
  /** `price` is the per-TB price, already formatted, e.g. `CHF 8`. */
  featureExtraStorage: (price: string) => string
  mostPopular: string
  /** `price` is the formatted yearly total, e.g. `CHF 168`. */
  billedYearly: (price: string) => string
  savePercent: (percent: number) => string
  /** `price` is the formatted per-month price on annual billing. */
  orAnnual: (price: string) => string
  perMonthShort: string
  /** aria-label for the mobile expand/collapse control on a plan row. */
  planToggleAria: (plan: string) => string
}

export const PRICING: Multilingual<PricingCopy> = {
  en: {
    heading: 'One price. The full suite.',
    intro:
      'Galleries, projects, invoicing, contracts, calendar, team chat — everything is included in every tier. No separate apps to buy, no per-feature upgrades.',
    compliance: 'VAT invoicing and reporting built in (incl. Swiss QR-bill) · Application, database and your files hosted in the EU (Germany/Netherlands)',
    modules: [
      'Client galleries',
      'Project management',
      'Invoicing & QR-bills',
      'Contracts',
      'Calendar sync',
      'Team chat',
    ],
    monthly: 'Monthly',
    annual: 'Annual',
    saveUpTo: (percent) => `Save up to ${percent}%`,
    planNames: { trial: 'Free trial', starter: 'Starter', studio: 'Studio', agency: 'Agency' },
    planDescs: {
      trial: 'The full suite, no credit card.',
      starter: 'Solo photographers getting started.',
      studio: 'Established studios with a team.',
      agency: 'High-volume studios, scale as you grow.',
    },
    planCtas: { starter: 'Get Starter', studio: 'Get Studio', agency: 'Get Agency' },
    trialPeriod: '30 days',
    perMonth: '/month',
    featureAll: 'Every feature included',
    featureStorage: (amount) => `${amount} storage`,
    featureSeats: (count) => `Up to ${count} team members`,
    featureUnlimitedSeats: 'Unlimited team members',
    featureExtraStorage: (price) => `Extra storage: ${price} / TB`,
    mostPopular: 'Most popular',
    billedYearly: (price) => `Billed ${price}/year`,
    savePercent: (percent) => `Save ${percent}%`,
    orAnnual: (price) => `Or ${price}/mo billed annually`,
    perMonthShort: '/mo',
    planToggleAria: (plan) => `Show what ${plan} includes`,
  },
  de: {
    heading: 'Ein Preis. Die ganze Suite.',
    intro:
      'Galerien, Projekte, Rechnungen, Verträge, Kalender, Team-Chat — in jedem Abo ist alles dabei. Keine Zusatz-Apps, keine Freischaltung pro Funktion.',
    compliance: 'MWST-Rechnungen und -Abrechnung inklusive (inkl. Schweizer QR-Rechnung) · Anwendung, Datenbank und deine Dateien in der EU gehostet (Deutschland/Niederlande)',
    modules: [
      'Kundengalerien',
      'Projektverwaltung',
      'Rechnungen & QR-Rechnung',
      'Verträge',
      'Kalender-Sync',
      'Team-Chat',
    ],
    monthly: 'Monatlich',
    annual: 'Jährlich',
    saveUpTo: (percent) => `Bis zu ${percent}% sparen`,
    planNames: { trial: 'Gratis testen', starter: 'Starter', studio: 'Studio', agency: 'Agency' },
    planDescs: {
      trial: 'Die ganze Suite, ohne Kreditkarte.',
      starter: 'Für Solo-Fotograf:innen am Anfang.',
      studio: 'Für etablierte Studios mit Team.',
      agency: 'Für Studios mit hohem Volumen.',
    },
    planCtas: { starter: 'Starter wählen', studio: 'Studio wählen', agency: 'Agency wählen' },
    trialPeriod: '30 Tage',
    perMonth: '/Monat',
    featureAll: 'Alle Funktionen inklusive',
    featureStorage: (amount) => `${amount} Speicher`,
    featureSeats: (count) => `Bis zu ${count} Teammitglieder`,
    featureUnlimitedSeats: 'Unbegrenzt Teammitglieder',
    featureExtraStorage: (price) => `Zusatzspeicher: ${price} / TB`,
    mostPopular: 'Am beliebtesten',
    billedYearly: (price) => `${price}/Jahr verrechnet`,
    savePercent: (percent) => `${percent}% sparen`,
    orAnnual: (price) => `Oder ${price}/Mt. bei jährlicher Zahlung`,
    perMonthShort: '/Mt.',
    planToggleAria: (plan) => `Zeigen, was ${plan} enthält`,
  },
  fr: {
    heading: 'Un prix. La suite complète.',
    intro:
      'Galeries, projets, facturation, contrats, calendrier, chat d’équipe — tout est inclus dans chaque formule. Aucune application à acheter en plus, aucune fonctionnalité à débloquer.',
    compliance: 'Facturation et décompte TVA intégrés (QR-facture suisse comprise) · Application, base de données et vos fichiers hébergés dans l’UE (Allemagne/Pays-Bas)',
    modules: [
      'Galeries clients',
      'Gestion de projets',
      'Facturation & QR-factures',
      'Contrats',
      'Synchronisation du calendrier',
      'Chat d’équipe',
    ],
    monthly: 'Mensuel',
    annual: 'Annuel',
    saveUpTo: (percent) => `Jusqu’à ${percent}% d’économie`,
    planNames: { trial: 'Essai gratuit', starter: 'Starter', studio: 'Studio', agency: 'Agency' },
    planDescs: {
      trial: 'La suite complète, sans carte de crédit.',
      starter: 'Pour les photographes indépendants qui démarrent.',
      studio: 'Pour les studios établis avec une équipe.',
      agency: 'Pour les studios à fort volume, évolutif.',
    },
    planCtas: { starter: 'Choisir Starter', studio: 'Choisir Studio', agency: 'Choisir Agency' },
    trialPeriod: '30 jours',
    perMonth: '/mois',
    featureAll: 'Toutes les fonctionnalités incluses',
    featureStorage: (amount) => `${amount} de stockage`,
    featureSeats: (count) => `Jusqu’à ${count} membres d’équipe`,
    featureUnlimitedSeats: 'Membres d’équipe illimités',
    featureExtraStorage: (price) => `Stockage supplémentaire : ${price} / TB`,
    mostPopular: 'Le plus choisi',
    billedYearly: (price) => `Facturé ${price}/an`,
    savePercent: (percent) => `${percent}% d’économie`,
    orAnnual: (price) => `Ou ${price}/mois en paiement annuel`,
    perMonthShort: '/mois',
    planToggleAria: (plan) => `Afficher ce que comprend ${plan}`,
  },
  it: {
    heading: 'Un prezzo. La suite completa.',
    intro:
      'Gallerie, progetti, fatture, contratti, calendario, chat di team — in ogni piano c’è tutto. Nessuna app da comprare a parte, nessuna funzione da sbloccare.',
    compliance: 'Fatturazione e rendiconto IVA inclusi (con QR-fattura svizzera) · Applicazione, database e i tuoi file ospitati nell’UE (Germania/Paesi Bassi)',
    modules: [
      'Gallerie clienti',
      'Gestione progetti',
      'Fatture & QR-fatture',
      'Contratti',
      'Sincronizzazione calendario',
      'Chat di team',
    ],
    monthly: 'Mensile',
    annual: 'Annuale',
    saveUpTo: (percent) => `Risparmia fino al ${percent}%`,
    planNames: { trial: 'Prova gratuita', starter: 'Starter', studio: 'Studio', agency: 'Agency' },
    planDescs: {
      trial: 'La suite completa, senza carta di credito.',
      starter: 'Per fotografi solo agli inizi.',
      studio: 'Per studi avviati con un team.',
      agency: 'Per studi con volumi elevati, cresce con te.',
    },
    planCtas: { starter: 'Scegli Starter', studio: 'Scegli Studio', agency: 'Scegli Agency' },
    trialPeriod: '30 giorni',
    perMonth: '/mese',
    featureAll: 'Tutte le funzioni incluse',
    featureStorage: (amount) => `${amount} di spazio`,
    featureSeats: (count) => `Fino a ${count} membri del team`,
    featureUnlimitedSeats: 'Membri del team illimitati',
    featureExtraStorage: (price) => `Spazio aggiuntivo: ${price} / TB`,
    mostPopular: 'Il più scelto',
    billedYearly: (price) => `${price}/anno fatturati`,
    savePercent: (percent) => `Risparmi il ${percent}%`,
    orAnnual: (price) => `Oppure ${price}/mese con pagamento annuale`,
    perMonthShort: '/mese',
    planToggleAria: (plan) => `Mostra cosa include ${plan}`,
  },
  es: {
    heading: 'Un precio. La suite completa.',
    intro:
      'Galerías, proyectos, facturación, contratos, calendario, chat de equipo: todo está incluido en cada plan. Sin aplicaciones aparte que comprar, sin funciones que desbloquear.',
    compliance: 'Facturación y declaración de IVA integradas (incl. factura QR suiza) · Aplicación, base de datos y sus archivos alojados en la UE (Alemania/Países Bajos)',
    modules: [
      'Galerías para clientes',
      'Gestión de proyectos',
      'Facturación y facturas QR',
      'Contratos',
      'Sincronización de calendario',
      'Chat de equipo',
    ],
    monthly: 'Mensual',
    annual: 'Anual',
    saveUpTo: (percent) => `Ahorre hasta un ${percent}%`,
    planNames: { trial: 'Prueba gratuita', starter: 'Starter', studio: 'Studio', agency: 'Agency' },
    planDescs: {
      trial: 'La suite completa, sin tarjeta de crédito.',
      starter: 'Para fotógrafos autónomos que empiezan.',
      studio: 'Para estudios consolidados con equipo.',
      agency: 'Para estudios de gran volumen, crece con usted.',
    },
    planCtas: { starter: 'Elegir Starter', studio: 'Elegir Studio', agency: 'Elegir Agency' },
    trialPeriod: '30 días',
    perMonth: '/mes',
    featureAll: 'Todas las funciones incluidas',
    featureStorage: (amount) => `${amount} de almacenamiento`,
    featureSeats: (count) => `Hasta ${count} miembros del equipo`,
    featureUnlimitedSeats: 'Miembros del equipo ilimitados',
    featureExtraStorage: (price) => `Almacenamiento adicional: ${price} / TB`,
    mostPopular: 'El más elegido',
    billedYearly: (price) => `${price}/año facturados`,
    savePercent: (percent) => `Ahorre un ${percent}%`,
    orAnnual: (price) => `O ${price}/mes con pago anual`,
    perMonthShort: '/mes',
    planToggleAria: (plan) => `Mostrar qué incluye ${plan}`,
  },
  pt: {
    heading: 'Um preço. A suíte completa.',
    intro:
      'Galerias, projetos, faturamento, contratos, calendário, chat de equipe — está tudo incluído em todos os planos. Sem apps extras para comprar, sem recurso para liberar.',
    compliance: 'Faturamento e apuração de IVA inclusos (com QR-bill suíço) · Aplicação, banco de dados e seus arquivos hospedados na UE (Alemanha/Países Baixos)',
    modules: [
      'Galerias para clientes',
      'Gestão de projetos',
      'Faturas e QR-bills',
      'Contratos',
      'Sincronização de calendário',
      'Chat de equipe',
    ],
    monthly: 'Mensal',
    annual: 'Anual',
    saveUpTo: (percent) => `Economize até ${percent}%`,
    planNames: { trial: 'Teste grátis', starter: 'Starter', studio: 'Studio', agency: 'Agency' },
    planDescs: {
      trial: 'A suíte completa, sem cartão de crédito.',
      starter: 'Para fotógrafos autônomos começando agora.',
      studio: 'Para estúdios estabelecidos com equipe.',
      agency: 'Para estúdios de alto volume, cresce com você.',
    },
    planCtas: { starter: 'Escolher Starter', studio: 'Escolher Studio', agency: 'Escolher Agency' },
    trialPeriod: '30 dias',
    perMonth: '/mês',
    featureAll: 'Todos os recursos inclusos',
    featureStorage: (amount) => `${amount} de armazenamento`,
    featureSeats: (count) => `Até ${count} membros da equipe`,
    featureUnlimitedSeats: 'Membros da equipe ilimitados',
    featureExtraStorage: (price) => `Armazenamento extra: ${price} / TB`,
    mostPopular: 'Mais escolhido',
    billedYearly: (price) => `${price}/ano cobrados`,
    savePercent: (percent) => `Economize ${percent}%`,
    orAnnual: (price) => `Ou ${price}/mês no plano anual`,
    perMonthShort: '/mês',
    planToggleAria: (plan) => `Mostrar o que o ${plan} inclui`,
  },
  hu: {
    heading: 'Egy ár. Minden benne van.',
    intro:
      'Galériák, projektek, számlázás, szerződések, naptár, csapatchat – minden csomagban minden benne van. Nincs külön megvásárolandó alkalmazás, nincs funkciónkénti felár.',
    compliance: 'Beépített ÁFA-s számlázás és ÁFA-kimutatás (svájci QR-számlával) · Az alkalmazás, az adatbázis és az Ön fájljainak tárhelye az EU-ban van (Németország/Hollandia)',
    modules: [
      'Ügyfélgalériák',
      'Projektkezelés',
      'Számlázás és QR-számlák',
      'Szerződések',
      'Naptárszinkron',
      'Csapatchat',
    ],
    monthly: 'Havi',
    annual: 'Éves',
    saveUpTo: (percent) => `Akár ${percent}% megtakarítás`,
    planNames: { trial: 'Ingyenes próba', starter: 'Starter', studio: 'Studio', agency: 'Agency' },
    planDescs: {
      trial: 'A teljes csomag, bankkártya nélkül.',
      starter: 'Most induló, egyedül dolgozó fotósoknak.',
      studio: 'Bejáratott, csapattal dolgozó stúdióknak.',
      agency: 'Nagy volumenű stúdióknak – a növekedéssel együtt bővül.',
    },
    planCtas: { starter: 'Starter választása', studio: 'Studio választása', agency: 'Agency választása' },
    trialPeriod: '30 nap',
    perMonth: '/hó',
    featureAll: 'Minden funkció benne van',
    featureStorage: (amount) => `${amount} tárhely`,
    featureSeats: (count) => `Legfeljebb ${count} csapattag`,
    featureUnlimitedSeats: 'Korlátlan számú csapattag',
    featureExtraStorage: (price) => `Extra tárhely: ${price} / TB`,
    mostPopular: 'Legnépszerűbb',
    billedYearly: (price) => `Éves számlázás: ${price}`,
    savePercent: (percent) => `${percent}% megtakarítás`,
    orAnnual: (price) => `Vagy ${price}/hó éves számlázással`,
    perMonthShort: '/hó',
    planToggleAria: (plan) => `A csomag tartalma: ${plan}`,
  },
  nl: {
    heading: 'Eén prijs. De complete suite.',
    intro:
      'Galerijen, projecten, facturatie, contracten, agenda, teamchat – alles zit in elk abonnement. Geen aparte apps om bij te kopen, geen extra kosten per functie.',
    compliance: 'Btw-facturatie en btw-rapportage ingebouwd (incl. Zwitserse QR-factuur) · Applicatie, database en uw bestanden gehost in de EU (Duitsland/Nederland)',
    modules: [
      'Klantgalerijen',
      'Projectbeheer',
      'Facturatie & QR-facturen',
      'Contracten',
      'Agendasynchronisatie',
      'Teamchat',
    ],
    monthly: 'Maandelijks',
    annual: 'Jaarlijks',
    saveUpTo: (percent) => `Bespaar tot ${percent}%`,
    planNames: { trial: 'Gratis proefperiode', starter: 'Starter', studio: 'Studio', agency: 'Agency' },
    planDescs: {
      trial: 'De complete suite, zonder creditcard.',
      starter: 'Voor solofotografen die net beginnen.',
      studio: 'Voor gevestigde studio’s met een team.',
      agency: 'Voor studio’s met een groot volume, groeit met u mee.',
    },
    planCtas: { starter: 'Starter kiezen', studio: 'Studio kiezen', agency: 'Agency kiezen' },
    trialPeriod: '30 dagen',
    perMonth: '/maand',
    featureAll: 'Alle functies inbegrepen',
    featureStorage: (amount) => `${amount} opslag`,
    featureSeats: (count) => `Tot ${count} teamleden`,
    featureUnlimitedSeats: 'Onbeperkt aantal teamleden',
    featureExtraStorage: (price) => `Extra opslag: ${price} / TB`,
    mostPopular: 'Meest gekozen',
    billedYearly: (price) => `${price}/jaar gefactureerd`,
    savePercent: (percent) => `${percent}% besparen`,
    orAnnual: (price) => `Of ${price}/mnd bij jaarlijkse betaling`,
    perMonthShort: '/mnd',
    planToggleAria: (plan) => `Toon wat ${plan} omvat`,
  },
  pl: {
    heading: 'Jedna cena. Pełny pakiet.',
    intro:
      'Galerie, projekty, fakturowanie, umowy, kalendarz, czat zespołu – wszystko jest w każdym pakiecie. Bez dokupowania osobnych aplikacji, bez dopłat za poszczególne funkcje.',
    compliance: 'Fakturowanie i raportowanie VAT w standardzie (w tym szwajcarski QR-rachunek) · Aplikacja, baza danych i pliki są hostowane w UE (Niemcy/Holandia)',
    modules: [
      'Galerie klientów',
      'Zarządzanie projektami',
      'Faktury i QR-rachunki',
      'Umowy',
      'Synchronizacja kalendarza',
      'Czat zespołu',
    ],
    monthly: 'Miesięcznie',
    annual: 'Rocznie',
    saveUpTo: (percent) => `Oszczędność do ${percent}%`,
    planNames: { trial: 'Bezpłatny okres próbny', starter: 'Starter', studio: 'Studio', agency: 'Agency' },
    planDescs: {
      trial: 'Pełny pakiet, bez karty kredytowej.',
      starter: 'Dla samodzielnych fotografów na początku drogi.',
      studio: 'Dla ugruntowanych studiów z zespołem.',
      agency: 'Dla studiów o dużym wolumenie – rośnie razem ze studiem.',
    },
    planCtas: { starter: 'Wybierz Starter', studio: 'Wybierz Studio', agency: 'Wybierz Agency' },
    trialPeriod: '30 dni',
    perMonth: '/miesiąc',
    featureAll: 'Wszystkie funkcje w cenie',
    featureStorage: (amount) => `${amount} miejsca na dane`,
    // "do N członków" would need the genitive plural to agree with every N;
    // putting the count after a colon keeps one form correct for any number.
    featureSeats: (count) => `Członkowie zespołu: do ${count}`,
    featureUnlimitedSeats: 'Nieograniczona liczba członków zespołu',
    featureExtraStorage: (price) => `Dodatkowe miejsce na dane: ${price} / TB`,
    mostPopular: 'Najczęściej wybierany',
    billedYearly: (price) => `Rozliczane rocznie: ${price}`,
    savePercent: (percent) => `Oszczędność ${percent}%`,
    orAnnual: (price) => `Lub ${price}/mies. przy płatności rocznej`,
    perMonthShort: '/mies.',
    planToggleAria: (plan) => `Pokaż, co zawiera: ${plan}`,
  },
  cs: {
    heading: 'Jedna cena. Celá sada.',
    intro:
      'Galerie, projekty, fakturace, smlouvy, kalendář, týmový chat – vše je součástí každého tarifu. Žádné další aplikace k dokoupení, žádné příplatky za jednotlivé funkce.',
    compliance: 'Fakturace a výkazy DPH v ceně (včetně švýcarské QR-faktury) · Aplikace, databáze i soubory jsou hostovány v EU (Německo/Nizozemsko)',
    modules: [
      'Klientské galerie',
      'Správa projektů',
      'Fakturace a QR-faktury',
      'Smlouvy',
      'Synchronizace kalendáře',
      'Týmový chat',
    ],
    monthly: 'Měsíčně',
    annual: 'Ročně',
    saveUpTo: (percent) => `Ušetřete až ${percent} %`,
    planNames: { trial: 'Zkušební období zdarma', starter: 'Starter', studio: 'Studio', agency: 'Agency' },
    planDescs: {
      trial: 'Celá sada, bez platební karty.',
      starter: 'Pro samostatné fotografy na začátku cesty.',
      studio: 'Pro zavedená studia s týmem.',
      agency: 'Pro studia s velkým objemem – roste spolu s vámi.',
    },
    planCtas: { starter: 'Vybrat Starter', studio: 'Vybrat Studio', agency: 'Vybrat Agency' },
    trialPeriod: '30 dní',
    perMonth: '/měsíc',
    featureAll: 'Všechny funkce v ceně',
    featureStorage: (amount) => `${amount} úložiště`,
    // Count after a colon: "členů/členové" would otherwise have to agree with N.
    featureSeats: (count) => `Členů týmu: až ${count}`,
    featureUnlimitedSeats: 'Neomezený počet členů týmu',
    featureExtraStorage: (price) => `Další úložiště: ${price} / TB`,
    mostPopular: 'Nejoblíbenější',
    billedYearly: (price) => `Fakturováno ročně: ${price}`,
    savePercent: (percent) => `Úspora ${percent} %`,
    orAnnual: (price) => `Nebo ${price}/měs. při roční platbě`,
    perMonthShort: '/měs.',
    planToggleAria: (plan) => `Zobrazit, co zahrnuje: ${plan}`,
  },
}

export interface WhySwitchCopy {
  heading: string
  headingAccent: string
  /**
   * Replaces the old invented-metric tiles. Each of these is a statement a
   * prospect can confirm inside the trial, not a number we made up.
   */
  facts: { title: string; note: string }[]
  replacingHeading: string
  replacements: [string, string][]
}

export const WHY_SWITCH: Multilingual<WhySwitchCopy> = {
  en: {
    heading: 'Why studios',
    headingAccent: 'switch.',
    facts: [
      {
        title: 'One login',
        note: 'Inbox, projects, galleries, contracts and invoices behind a single sign-in.',
      },
      {
        title: 'Invoicing & VAT built in',
        note: 'VAT invoices and VAT reporting for European studios, Swiss QR-bill included — part of the product, not an add-on.',
      },
      {
        title: 'Eleven languages',
        note: 'Client pages and client emails render in the client’s own language — eleven languages in all — and your team works in the app in all eleven.',
      },
      {
        title: 'Designed mobile-first',
        note: 'Every workflow was designed for one-handed phone use first, then scaled up to the desktop.',
      },
    ],
    replacingHeading: 'Everything you’re juggling today',
    replacements: [
      ['A separate task-management app', 'Built-in tasks with team assignments'],
      ['A separate invoicing tool', 'VAT invoices with Swiss QR-bill support'],
      ['Price lists in spreadsheets', 'Service catalog with one-click quoting'],
      ['Email forwarding', 'Inbox sync with a triage workflow'],
      ['Team chat scattered across messaging apps', 'In-app team chat — conversations in context'],
      ['A separate contracts tool', 'Template-based contracts with accept links'],
      ['A separate form and questionnaire tool', 'Multilingual client questionnaires'],
      ['A separate gallery-delivery tool', 'Built-in customisable client galleries'],
      ['Desktop-only tools', 'Mobile-first UI — every workflow works on a phone'],
    ],
  },
  de: {
    heading: 'Warum Studios',
    headingAccent: 'wechseln.',
    facts: [
      {
        title: 'Ein Login',
        note: 'Posteingang, Projekte, Galerien, Verträge und Rechnungen hinter einer Anmeldung.',
      },
      {
        title: 'Rechnungen & MWST inklusive',
        note: 'MWST-Rechnungen und MWST-Abrechnung für europäische Studios, Schweizer QR-Rechnung inklusive — Teil des Produkts, kein Zusatzmodul.',
      },
      {
        title: 'Elf Sprachen',
        note: 'Kundenseiten und Kunden-E-Mails erscheinen in der Sprache der Kundschaft — in elf Sprachen —, und dein Team arbeitet in der App in allen elf.',
      },
      {
        title: 'Mobile-first entworfen',
        note: 'Jeder Ablauf ist zuerst für die einhändige Bedienung am Handy entworfen und danach auf den Desktop skaliert.',
      },
    ],
    replacingHeading: 'Was du heute alles jonglierst',
    replacements: [
      ['Eine separate Aufgaben-App', 'Aufgaben mit Team-Zuweisung direkt drin'],
      ['Ein separates Rechnungstool', 'MWST-Rechnungen mit Schweizer QR-Rechnung'],
      ['Preislisten in Tabellen', 'Leistungskatalog mit Offerte auf einen Klick'],
      ['E-Mail-Weiterleitungen', 'Posteingang-Sync mit Triage-Ablauf'],
      ['Team-Chat verstreut über Messenger-Apps', 'Team-Chat in der App — Gespräche im Kontext'],
      ['Ein separates Vertragstool', 'Verträge aus Vorlagen mit Annahme-Link'],
      ['Ein separates Formular- und Fragebogen-Tool', 'Mehrsprachige Kundenfragebogen'],
      ['Ein separates Galerie-Tool', 'Anpassbare Kundengalerien direkt drin'],
      ['Nur-Desktop-Tools', 'Mobile-first — jeder Ablauf läuft auf dem Handy'],
    ],
  },
  fr: {
    heading: 'Pourquoi les studios',
    headingAccent: 'changent.',
    facts: [
      {
        title: 'Un seul identifiant',
        note: 'Boîte de réception, projets, galeries, contrats et factures derrière une seule connexion.',
      },
      {
        title: 'Facturation et TVA intégrées',
        note: 'Factures et décomptes TVA pour les studios européens, QR-facture suisse comprise — dans le produit, pas en supplément.',
      },
      {
        title: 'Onze langues',
        note: 'Les pages et les e-mails destinés au client s’affichent dans la langue du client — onze langues au total —, et votre équipe travaille dans l’application dans les onze.',
      },
      {
        title: 'Conçu mobile-first',
        note: 'Chaque flux de travail a d’abord été conçu pour une utilisation à une main sur téléphone, puis élargi au bureau.',
      },
    ],
    replacingHeading: 'Tout ce que vous jonglez aujourd’hui',
    replacements: [
      ['Une application de tâches séparée', 'Des tâches intégrées, attribuées à l’équipe'],
      ['Un outil de facturation séparé', 'Des factures TVA avec QR-facture suisse'],
      ['Des tarifs dans des tableurs', 'Un catalogue de prestations et une offre en un clic'],
      ['Des redirections d’e-mails', 'Une boîte de réception synchronisée avec un flux de tri'],
      ['Un chat d’équipe éparpillé sur des messageries', 'Un chat d’équipe intégré — les échanges dans leur contexte'],
      ['Un outil de contrats séparé', 'Des contrats sur modèles avec lien d’acceptation'],
      ['Un outil de formulaires et de questionnaires séparé', 'Des questionnaires clients multilingues'],
      ['Un outil de livraison de galeries séparé', 'Des galeries clients personnalisables intégrées'],
      ['Des outils réservés au bureau', 'Une interface mobile-first — chaque flux fonctionne sur téléphone'],
    ],
  },
  it: {
    heading: 'Perché gli studi',
    headingAccent: 'cambiano.',
    facts: [
      {
        title: 'Un solo accesso',
        note: 'Posta in arrivo, progetti, gallerie, contratti e fatture dietro un unico login.',
      },
      {
        title: 'Fatture e IVA incluse',
        note: 'Fatture e rendiconti IVA per gli studi europei, QR-fattura svizzera compresa — parte del prodotto, non un modulo a parte.',
      },
      {
        title: 'Undici lingue',
        note: 'Le pagine e le email per i clienti compaiono nella lingua del cliente — undici lingue in tutto — e il tuo team lavora nell’app in tutte e undici.',
      },
      {
        title: 'Progettato mobile-first',
        note: 'Ogni flusso di lavoro è stato pensato prima per l’uso con una mano sul telefono e poi ampliato al desktop.',
      },
    ],
    replacingHeading: 'Tutto quello che oggi tieni insieme a fatica',
    replacements: [
      ['Un’app separata per le attività', 'Attività integrate, assegnate al team'],
      ['Uno strumento di fatturazione separato', 'Fatture IVA con QR-fattura svizzera'],
      ['Listini nei fogli di calcolo', 'Catalogo servizi con preventivo in un clic'],
      ['Inoltri di email', 'Posta in arrivo sincronizzata con flusso di smistamento'],
      ['Chat di team sparsa tra app di messaggistica', 'Chat di team nell’app — conversazioni nel contesto'],
      ['Uno strumento separato per i contratti', 'Contratti da modelli con link di accettazione'],
      ['Uno strumento separato per moduli e questionari', 'Questionari per i clienti multilingue'],
      ['Uno strumento separato per consegnare le gallerie', 'Gallerie clienti personalizzabili integrate'],
      ['Strumenti solo da desktop', 'Interfaccia mobile-first — ogni flusso funziona sul telefono'],
    ],
  },
  es: {
    heading: 'Por qué los estudios',
    headingAccent: 'cambian.',
    facts: [
      {
        title: 'Un solo acceso',
        note: 'Bandeja de entrada, proyectos, galerías, contratos y facturas tras un único inicio de sesión.',
      },
      {
        title: 'Facturación e IVA incluidos',
        note: 'Facturas y declaraciones de IVA para estudios europeos, con la factura QR suiza — parte del producto, no un complemento.',
      },
      {
        title: 'Once idiomas',
        note: 'Las páginas y los correos dirigidos al cliente se muestran en el idioma del cliente — once idiomas en total —, y su equipo trabaja en la aplicación en los once.',
      },
      {
        title: 'Diseñado mobile-first',
        note: 'Cada flujo de trabajo se diseñó primero para usarlo con una mano en el móvil y después se amplió al escritorio.',
      },
    ],
    replacingHeading: 'Todo lo que hoy tiene que compaginar',
    replacements: [
      ['Una aplicación de tareas aparte', 'Tareas integradas, asignadas al equipo'],
      ['Una herramienta de facturación aparte', 'Facturas con IVA y factura QR suiza'],
      ['Listas de precios en hojas de cálculo', 'Catálogo de servicios con presupuesto en un clic'],
      ['Reenvíos de correo', 'Buzón sincronizado con un flujo de clasificación'],
      ['Chat de equipo repartido entre apps de mensajería', 'Chat de equipo en la aplicación: las conversaciones en su contexto'],
      ['Una herramienta de contratos aparte', 'Contratos a partir de plantillas con enlace de aceptación'],
      ['Una herramienta de formularios y cuestionarios aparte', 'Cuestionarios para clientes multilingües'],
      ['Una herramienta de entrega de galerías aparte', 'Galerías para clientes personalizables integradas'],
      ['Herramientas solo para escritorio', 'Interfaz mobile-first: cada flujo funciona en el móvil'],
    ],
  },
  pt: {
    heading: 'Por que os estúdios',
    headingAccent: 'mudam.',
    facts: [
      {
        title: 'Um único login',
        note: 'Caixa de entrada, projetos, galerias, contratos e faturas atrás de um único login.',
      },
      {
        title: 'Faturamento e IVA inclusos',
        note: 'Faturas e apuração de IVA para estúdios europeus, com QR-bill suíço — parte do produto, não um complemento.',
      },
      {
        title: 'Onze idiomas',
        note: 'As páginas e os e-mails para o cliente aparecem no idioma do cliente — onze idiomas no total — e sua equipe trabalha no app em todos os onze.',
      },
      {
        title: 'Projetado mobile-first',
        note: 'Cada fluxo de trabalho foi pensado primeiro para o uso com uma só mão no celular e depois ampliado para o desktop.',
      },
    ],
    replacingHeading: 'Tudo o que você equilibra hoje',
    replacements: [
      ['Um app de tarefas separado', 'Tarefas integradas, atribuídas à equipe'],
      ['Uma ferramenta de faturamento separada', 'Faturas com IVA e QR-bill suíço'],
      ['Tabelas de preços em planilhas', 'Catálogo de serviços com orçamento em um clique'],
      ['Encaminhamentos de e-mail', 'Caixa de entrada sincronizada com fluxo de triagem'],
      ['Chat de equipe espalhado por apps de mensagem', 'Chat de equipe no app — conversas no contexto'],
      ['Uma ferramenta de contratos separada', 'Contratos a partir de modelos com link de aceite'],
      ['Uma ferramenta de formulários e questionários separada', 'Questionários multilíngues para clientes'],
      ['Uma ferramenta de entrega de galerias separada', 'Galerias para clientes personalizáveis integradas'],
      ['Ferramentas só para desktop', 'Interface mobile-first — cada fluxo funciona no celular'],
    ],
  },
  hu: {
    heading: 'Ezért váltanak',
    headingAccent: 'a stúdiók.',
    facts: [
      {
        title: 'Egyetlen bejelentkezés',
        note: 'Postafiók, projektek, galériák, szerződések és számlák – mind egyetlen bejelentkezés mögött.',
      },
      {
        title: 'Beépített számlázás és ÁFA',
        note: 'ÁFA-s számlák és ÁFA-kimutatás európai stúdióknak, svájci QR-számlával – a termék része, nem kiegészítő.',
      },
      {
        title: 'Tizenegy nyelv',
        note: 'Az ügyféloldalak és az ügyfeleknek küldött e-mailek az ügyfél saját nyelvén jelennek meg – tizenegy nyelven. Csapata mind a tizenegy nyelven dolgozhat az alkalmazásban.',
      },
      {
        title: 'Mobilra tervezve',
        note: 'Minden munkafolyamatot először egykezes, telefonos használatra terveztünk, és csak utána bővítettük asztali gépre.',
      },
    ],
    replacingHeading: 'Minden, amit ma egyszerre kell kézben tartania',
    replacements: [
      ['Külön feladatkezelő alkalmazás', 'Beépített feladatok, csapattagokra kiosztva'],
      ['Külön számlázóprogram', 'ÁFA-s számlák svájci QR-számlával'],
      ['Árlisták táblázatokban', 'Szolgáltatáskatalógus, ajánlat egy kattintással'],
      ['E-mail-továbbítás', 'Szinkronizált postafiók, beépített rendszerezéssel'],
      ['Csapatchat szétszórva több üzenetküldő alkalmazásban', 'Csapatchat az alkalmazásban – a beszélgetések a helyükön'],
      ['Külön szerződéskezelő eszköz', 'Sablonalapú szerződések elfogadási linkkel'],
      ['Külön űrlap- és kérdőíveszköz', 'Többnyelvű ügyfélkérdőívek'],
      ['Külön galériaszolgáltatás', 'Beépített, testreszabható ügyfélgalériák'],
      ['Csak asztali gépen működő eszközök', 'Mobilra tervezett felület – minden munkafolyamat működik telefonon'],
    ],
  },
  nl: {
    heading: 'Waarom studio’s',
    headingAccent: 'overstappen.',
    facts: [
      {
        title: 'Eén login',
        note: 'Inbox, projecten, galerijen, contracten en facturen achter één inlog.',
      },
      {
        title: 'Facturatie & btw ingebouwd',
        note: 'Btw-facturen en btw-rapportage voor Europese studio’s, Zwitserse QR-factuur inbegrepen – onderdeel van het product, geen add-on.',
      },
      {
        title: 'Elf talen',
        note: 'Klantpagina’s en e-mails aan klanten verschijnen in de eigen taal van de klant – in elf talen – en uw team werkt in de app in alle elf.',
      },
      {
        title: 'Mobile-first ontworpen',
        note: 'Elke workflow is eerst ontworpen voor gebruik met één hand op de telefoon en daarna opgeschaald naar de desktop.',
      },
    ],
    replacingHeading: 'Alles wat u nu tegelijk bijhoudt',
    replacements: [
      ['Een aparte takenapp', 'Ingebouwde taken met toewijzing aan het team'],
      ['Een aparte facturatietool', 'Btw-facturen met Zwitserse QR-factuur'],
      ['Prijslijsten in spreadsheets', 'Dienstencatalogus met offertes met één klik'],
      ['Doorgestuurde e-mails', 'Gesynchroniseerde inbox met een triage-workflow'],
      ['Teamchat verspreid over berichtenapps', 'Teamchat in de app – gesprekken in context'],
      ['Een aparte contractentool', 'Contracten op basis van sjablonen met acceptatielink'],
      ['Een aparte tool voor formulieren en vragenlijsten', 'Meertalige klantvragenlijsten'],
      ['Een aparte tool voor galerij-oplevering', 'Ingebouwde, aanpasbare klantgalerijen'],
      ['Tools die alleen op de desktop werken', 'Mobile-first interface – elke workflow werkt op een telefoon'],
    ],
  },
  pl: {
    heading: 'Dlaczego studia',
    headingAccent: 'się przesiadają.',
    facts: [
      {
        title: 'Jedno logowanie',
        note: 'Skrzynka, projekty, galerie, umowy i faktury za jednym logowaniem.',
      },
      {
        title: 'Fakturowanie i VAT w standardzie',
        note: 'Faktury z VAT i raportowanie VAT dla europejskich studiów, w tym szwajcarski QR-rachunek – część produktu, a nie dodatek.',
      },
      {
        title: 'Jedenaście języków',
        note: 'Strony i e-maile dla klientów wyświetlają się w języku klienta – w jedenastu językach – a zespół pracuje w aplikacji we wszystkich jedenastu.',
      },
      {
        title: 'Zaprojektowane mobile-first',
        note: 'Każdy proces został najpierw zaprojektowany z myślą o obsłudze jedną ręką na telefonie, a dopiero potem rozszerzony na komputer.',
      },
    ],
    replacingHeading: 'Wszystko, co dziś trzeba mieć pod kontrolą jednocześnie',
    replacements: [
      ['Osobna aplikacja do zadań', 'Wbudowane zadania przydzielane zespołowi'],
      ['Osobne narzędzie do faktur', 'Faktury z VAT i szwajcarskim QR-rachunkiem'],
      ['Cenniki w arkuszach kalkulacyjnych', 'Katalog usług i oferta jednym kliknięciem'],
      ['Przekazywanie e-maili dalej', 'Zsynchronizowana skrzynka z procesem sortowania'],
      ['Czat zespołu rozproszony po komunikatorach', 'Czat zespołu w aplikacji – rozmowy w kontekście'],
      ['Osobne narzędzie do umów', 'Umowy z szablonów z linkiem do akceptacji'],
      ['Osobne narzędzie do formularzy i kwestionariuszy', 'Wielojęzyczne kwestionariusze dla klientów'],
      ['Osobne narzędzie do dostarczania galerii', 'Wbudowane, konfigurowalne galerie klientów'],
      ['Narzędzia tylko na komputer', 'Interfejs mobile-first – każdy proces działa na telefonie'],
    ],
  },
  cs: {
    heading: 'Proč studia',
    headingAccent: 'přecházejí.',
    facts: [
      {
        title: 'Jedno přihlášení',
        note: 'Doručená pošta, projekty, galerie, smlouvy a faktury za jedním přihlášením.',
      },
      {
        title: 'Fakturace a DPH v ceně',
        note: 'Faktury s DPH a výkazy DPH pro evropská studia, včetně švýcarské QR-faktury – součást produktu, ne doplněk.',
      },
      {
        title: 'Jedenáct jazyků',
        note: 'Stránky a e-maily pro klienty se zobrazují v jazyce klienta – v jedenácti jazycích – a váš tým pracuje v aplikaci ve všech jedenácti.',
      },
      {
        title: 'Navrženo mobile-first',
        note: 'Každý postup byl nejdřív navržen pro ovládání jednou rukou na telefonu a teprve potom rozšířen na počítač.',
      },
    ],
    replacingHeading: 'Všechno, co dnes žonglujete zároveň',
    replacements: [
      ['Samostatná aplikace na úkoly', 'Vestavěné úkoly s přidělením týmu'],
      ['Samostatný nástroj na fakturaci', 'Faktury s DPH a švýcarskou QR-fakturou'],
      ['Ceníky v tabulkách', 'Katalog služeb a nabídka jedním kliknutím'],
      ['Přeposílání e-mailů', 'Synchronizovaná doručená pošta s procesem třídění'],
      ['Týmový chat roztroušený po komunikátorech', 'Týmový chat v aplikaci – rozhovory v kontextu'],
      ['Samostatný nástroj na smlouvy', 'Smlouvy ze šablon s odkazem k přijetí'],
      ['Samostatný nástroj na formuláře a dotazníky', 'Vícejazyčné klientské dotazníky'],
      ['Samostatný nástroj na předávání galerií', 'Vestavěné přizpůsobitelné klientské galerie'],
      ['Nástroje jen pro počítač', 'Rozhraní mobile-first – každý postup funguje na telefonu'],
    ],
  },
}

/**
 * Strings that must never be rendered on any marketing surface. Kept here as
 * data so the guard test and the human writing the next page read the same list.
 *
 * Six languages now, not two. A forbidden claim does not become allowed by
 * being made in French: "hébergement suisse" is the same false statement as
 * "Swiss hosting", and a translator reaching for the natural rendering of a
 * hosting sentence is exactly how one would get back in. The list is matched
 * case-insensitively as a substring, so each entry is the shortest fragment
 * that is unambiguously the forbidden claim and cannot appear inside a
 * legitimate one ("hébergés dans l'UE" must keep passing).
 */
export const FORBIDDEN_CLAIM_PHRASES = [
  // Nothing runs in Switzerland — app and DB are at Hetzner in Germany, files
  // in Backblaze B2's EU region in the Netherlands.
  'Swiss hosting',
  'Schweizer Hosting',
  'hébergement suisse',
  'hébergé en Suisse',
  'hébergés en Suisse',
  'hosting svizzero',
  'ospitato in Svizzera',
  'ospitati in Svizzera',
  'alojamiento suizo',
  'alojado en Suiza',
  'alojados en Suiza',
  'hospedagem suíça',
  'hospedado na Suíça',
  'hospedados na Suíça',
  // Still forbidden after the 2026-08-19 EU-storage upgrade: transactional
  // email transits Resend in the US, so nothing here may say the data stays put.
  'data never leaves',
  'Daten verlassen',
  'ne quittent jamais',
  'non lasciano mai',
  'nunca salen',
  'nunca saem',
  // Residency is not sovereignty. Storing files in Amsterdam does not make the
  // stack EU-sovereign while Backblaze, Cloudflare, Resend and Stripe are all
  // US-incorporated — it is a term with a legal meaning we cannot satisfy.
  'EU-sovereign',
  'EU sovereign',
  'EU-souverän',
  'EU-Souveränität',
  'souveraineté européenne',
  'souveraineté numérique',
  'sovranità europea',
  'sovranità digitale',
  'soberanía europea',
  'soberanía digital',
  'soberania europeia',
  'soberania digital',
  // Hungarian (RB-12 C4) — the same three false claims.
  'svájci tárhely',
  'svájci szerver',
  'Svájcban tárol',
  'Svájcban üzemel',
  'soha nem hagyj',
  'EU-szuverén',
  'európai szuverenitás',
  'digitális szuverenitás',
  // Dutch, Polish, Czech (RB-43) — the same three false claims. Polish and Czech
  // are inflected, so each entry is the shortest stem-stable fragment.
  'Zwitserse hosting',
  'gehost in Zwitserland',
  'data verlaat nooit',
  'gegevens verlaten nooit',
  'EU-soeverein',
  'Europese soevereiniteit',
  'digitale soevereiniteit',
  'szwajcarski hosting',
  'hostowane w Szwajcarii',
  'hostowany w Szwajcarii',
  'dane nigdy nie opuszczają',
  'suwerenność UE',
  'suwerenność europejska',
  'suwerenność cyfrowa',
  'švýcarský hosting',
  'hostováno ve Švýcarsku',
  'hostovány ve Švýcarsku',
  'data nikdy neopouštějí',
  'evropská suverenita',
  'digitální suverenita',
  'suverenita EU',
] as const
